import { useMemo } from "react";
import { useSearchParams } from "react-router";
import i18n from "~/i18n";

type GetValue<T> = (item: T) => string | string[];

export interface Filter<T> {
  id: string;
  label: string;
  options: {
    value: string;
    label: string;
  }[];
  getValue: GetValue<T>;
  /** Lets several options be picked at once, matching any of them. */
  isMulti?: boolean;
}

export interface FilterWithOption<T> extends Filter<T> {
  optionIds: string[];
}

export const ALL_OPTION_VALUE = "all";

const allOption = {
  value: ALL_OPTION_VALUE,
  label: i18n.t(($) => $.commons.all),
};

/** Whether an item passes a filter. Filters with nothing picked pass everything. */
const matchesFilter = <T>(
  item: T,
  { optionIds, getValue }: FilterWithOption<T>,
) => {
  if (optionIds.length === 0) return true;

  const itemValue = getValue(item);
  return Array.isArray(itemValue)
    ? itemValue.some((value) => optionIds.includes(value))
    : optionIds.includes(itemValue);
};

export function useFilter<T>(filterInput: Filter<T>[]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const filters = useMemo(() => {
    // Rebuild URLSearchParams from the stable string so the dependency array only contains simple expressions
    const params = new URLSearchParams(searchParamsString);
    return filterInput.map((filter) => {
      const known = new Set(filter.options.map((option) => option.value));
      return {
        ...filter,
        // A single-select is cleared through its "all" option, a multi-select by
        // deselecting, so only the former needs the extra option.
        options: filter.isMulti
          ? filter.options
          : [allOption, ...filter.options],
        optionIds:
          params
            .get(filter.id)
            ?.split(",")
            .filter((value) => known.has(value)) ?? [],
      };
    });
  }, [filterInput, searchParamsString]);

  const handleSelectFilter = (filterId: string, optionIds: string[]) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        const selected = optionIds.filter(
          (optionId) => optionId !== ALL_OPTION_VALUE,
        );

        if (selected.length === 0) {
          newParams.delete(filterId);
        } else {
          newParams.set(filterId, selected.join(","));
        }
        return newParams;
      },
      { replace: true },
    );
  };

  const handleClearFilters = () => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        filters.forEach((filter) => {
          newParams.delete(filter.id);
        });
        return newParams;
      },
      { replace: true },
    );
  };

  const activeFilters = filters.filter((filter) => filter.optionIds.length > 0);

  const filterItems = (items: T[]) =>
    items.filter((item) =>
      activeFilters.every((filter) => matchesFilter(item, filter)),
    );

  return {
    filters,
    activeFilters,
    handleClearFilters,
    handleSelectFilter,
    filterItems,
  };
}
